import { createFileRoute, Link } from '@tanstack/react-router';
import { SHIRES, type ShireId } from '../components/shared/electorate-shires';

export const Route = createFileRoute('/calendar')({
  component: CalendarPage,
  head: () => ({
    meta: [
      { title: 'Council meetings calendar — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Combined calendar of upcoming ordinary council meetings across the six Warren-Blackwood shires — Augusta-Margaret River, Denmark, Manjimup, Nannup, Bridgetown-Greenbushes and Boyup Brook.',
      },
      { property: 'og:title', content: 'Council meetings calendar — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'One view of upcoming ordinary meetings for all six Warren-Blackwood shire councils.',
      },
    ],
  }),
});

/** Day-of-week numbers Sun=0..Sat=6. */
type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface MeetingRule {
  shireId: ShireId;
  /** Which weekday (0=Sun). */
  weekday: Weekday;
  /** Nth occurrence of that weekday in the month (1-5), or 'last'. */
  ordinal: 1 | 2 | 3 | 4 | 5 | 'last';
  /** Plain-English start time, if published. */
  time?: string;
  /** Months (1-12) when council does NOT meet — typically Jan recess. */
  excludedMonths?: number[];
  /** Note about location if it isn't the council office address. */
  locationNote?: string;
}

const MEETING_RULES: MeetingRule[] = [
  {
    shireId: 'augusta-margaret-river',
    weekday: 3,
    ordinal: 4,
    time: '5:30pm',
    excludedMonths: [],
    locationNote: 'Some meetings are held in Augusta — check the shire website.',
  },
  {
    shireId: 'nannup',
    weekday: 4,
    ordinal: 4,
    time: '4:30pm',
    excludedMonths: [1],
  },
  {
    shireId: 'manjimup',
    weekday: 3,
    ordinal: 4,
    time: '9:30am',
    excludedMonths: [1],
  },
  {
    shireId: 'bridgetown-greenbushes',
    weekday: 4,
    ordinal: 4,
    time: '5:30pm',
    excludedMonths: [1],
  },
  {
    shireId: 'boyup-brook',
    weekday: 4,
    ordinal: 'last',
    excludedMonths: [],
  },
  {
    shireId: 'denmark',
    weekday: 2,
    ordinal: 'last',
    time: '4:00pm',
    excludedMonths: [1],
  },
];

/**
 * Return the date of the Nth weekday in a given month, or null if it doesn't
 * exist. `ordinal` may be 'last' for the final occurrence in the month.
 */
function nthWeekdayOfMonth(
  year: number,
  month0: number,
  weekday: number,
  ordinal: number | 'last',
): Date | null {
  if (ordinal === 'last') {
    const lastDay = new Date(year, month0 + 1, 0);
    const back = (lastDay.getDay() - weekday + 7) % 7;
    return new Date(year, month0, lastDay.getDate() - back);
  }
  const first = new Date(year, month0, 1);
  const shift = (weekday - first.getDay() + 7) % 7;
  const day = 1 + shift + (ordinal - 1) * 7;
  const result = new Date(year, month0, day);
  if (result.getMonth() !== month0) return null;
  return result;
}

interface UpcomingMeeting {
  shireId: ShireId;
  date: Date;
  rule: MeetingRule;
}

function computeUpcoming(rules: MeetingRule[], from: Date, count = 3): UpcomingMeeting[] {
  const out: UpcomingMeeting[] = [];
  for (const rule of rules) {
    let year = from.getFullYear();
    let month0 = from.getMonth();
    let found = 0;
    while (found < count && year < from.getFullYear() + 2) {
      const month1 = month0 + 1;
      if (!rule.excludedMonths?.includes(month1)) {
        const d = nthWeekdayOfMonth(year, month0, rule.weekday, rule.ordinal);
        if (d && d.getTime() >= from.getTime()) {
          out.push({ shireId: rule.shireId, date: d, rule });
          found++;
        }
      }
      month0++;
      if (month0 > 11) {
        month0 = 0;
        year++;
      }
    }
  }
  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDate(d: Date): string {
  return `${WEEKDAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function CalendarPage() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const upcoming = computeUpcoming(MEETING_RULES, today, 3);

  // Group by month
  const grouped = new Map<string, UpcomingMeeting[]>();
  for (const m of upcoming) {
    const key = `${m.date.getFullYear()}-${String(m.date.getMonth() + 1).padStart(2, '0')}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(m);
  }
  const groupKeys = Array.from(grouped.keys()).sort();

  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.35rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-lg)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  const meetingCard: React.CSSProperties = {
    background: '#fff',
    border: '1px solid var(--rule)',
    borderRadius: 8,
    padding: '0.85rem 1rem',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '0.5rem 1rem',
    alignItems: 'start',
  };
  const dateBox: React.CSSProperties = {
    background: 'var(--gold-pale)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    padding: '0.4rem 0.6rem',
    textAlign: 'center',
    minWidth: 70,
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Calendar · Council meetings
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Council meetings calendar
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.5,
        }}
      >
        Upcoming ordinary council meetings across all six Warren-Blackwood
        shires — Augusta-Margaret River, Boyup Brook, Bridgetown-Greenbushes,
        Denmark, Manjimup and Nannup — in one view. All meetings are open
        to the public.
      </p>

      <div style={callout('#fff7e6', '#f0c674')}>
        <strong style={{ color: '#7a5300' }}>Confirm before you drive</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.92rem' }}>
          Dates below are calculated from each council's published meeting
          cadence (e.g. "last Thursday of the month"), and exclude January
          where that council takes a recess. Councils can move, cancel, or relocate meetings — always
          check the official agenda link before travelling, especially for
          special meetings, electors' meetings and concept forums.
        </p>
      </div>

      {groupKeys.map((key) => {
        const [yearStr, monthStr] = key.split('-');
        const monthIdx = Number(monthStr) - 1;
        const meetings = grouped.get(key)!;
        return (
          <section key={key}>
            <h2 style={h2}>{MONTH_NAMES[monthIdx]} {yearStr}</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.6rem' }}>
              {meetings.map((m) => {
                const shire = SHIRES.find((s) => s.id === m.shireId)!;
                return (
                  <li key={`${m.shireId}-${isoDate(m.date)}`} style={meetingCard}>
                    <div style={dateBox}>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-mid)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {WEEKDAY_NAMES[m.date.getDay()].slice(0, 3)}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: '1.4rem',
                          color: 'var(--green)',
                          lineHeight: 1,
                          margin: '2px 0',
                        }}
                      >
                        {m.date.getDate()}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-mid)' }}>
                        {MONTH_NAMES[m.date.getMonth()].slice(0, 3)}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: '1.05rem',
                          color: 'var(--ink)',
                          lineHeight: 1.25,
                        }}
                      >
                        Shire of {shire.name}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--ink-mid)', marginTop: 2 }}>
                        Ordinary Council Meeting{m.rule.time ? ` · ${m.rule.time}` : ''}
                      </div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--ink)', marginTop: 4 }}>
                        Council chambers — {shire.council?.address ?? 'see shire website'}
                      </div>
                      {m.rule.locationNote && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--ink-mid)', fontStyle: 'italic', marginTop: 2 }}>
                          {m.rule.locationNote}
                        </div>
                      )}
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '0.85rem' }}>
                        {shire.meetings?.url && (
                          <a
                            href={shire.meetings.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ ...link, fontSize: '0.88rem' }}
                          >
                            Agenda &amp; minutes →
                          </a>
                        )}
                        <Link
                          to="/shire/$name"
                          params={{ name: shire.id }}
                          style={{ ...link, fontSize: '0.88rem' }}
                        >
                          Shire page →
                        </Link>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <h2 style={h2}>Each council's regular cadence</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        {MEETING_RULES.map((r) => {
          const shire = SHIRES.find((s) => s.id === r.shireId)!;
          const ordinals = ['1st', '2nd', '3rd', '4th', '5th'];
          const ordinalLabel = r.ordinal === 'last' ? 'last' : ordinals[r.ordinal - 1];
          const noJanuary = r.excludedMonths?.includes(1);
          return (
            <li key={r.shireId} style={{ marginBottom: '0.35rem' }}>
              <strong>Shire of {shire.name}</strong> — {ordinalLabel}{' '}
              {WEEKDAY_NAMES[r.weekday]} of the month
              {r.time ? `, ${r.time}` : ''}
              {noJanuary ? ' (no meeting in January)' : ''}.
              {shire.meetings?.url && (
                <>
                  {' '}
                  <a
                    href={shire.meetings.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={link}
                  >
                    official schedule →
                  </a>
                </>
              )}
            </li>
          );
        })}
      </ul>

      <h2 style={h2}>How to attend or speak</h2>
      <p style={p}>
        Ordinary meetings are open to the public. Most WA councils provide
        a <strong>public question time</strong> near the start of the
        meeting. To ask a formal question, councils generally appreciate
        questions being lodged in writing a day or two ahead — contact your
        shire's governance officer through the shire website. Agendas
        usually publish 3–5 days before the meeting.
      </p>
      <p style={p}>
        For one-off items, you can usually make a <strong>deputation</strong>{' '}
        (a short presentation to councillors on an agenda item) by writing
        to the CEO ahead of time. Each shire's rules vary slightly — check
        the council's Standing Orders or contact governance.
      </p>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Dates calculated from each council's published meeting cadence —
        not pulled from live agendas. Always confirm via the linked
        official agenda before travelling. Spot a cadence change?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20council%20calendar%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
